import { defineModule } from '@nexus/core';
import {
  inspectionCreated,
  inspectionDeleted,
  inspectionStatusChanged,
  inspectionUpdated,
} from './events';

const READ = 'sigeko.inspection.read';
const WRITE = 'sigeko.inspection.write';

/** Contract between the module and the kernel (docs/architecture/modules.md). */
export const manifest = defineModule({
  id: 'sigeko',
  name: 'sigeko.name',
  description: 'sigeko.description',
  icon: 'hard-hat',
  theme: { accent: 'amber' },
  // Construction projects and buildings come from projects, clients and owners from crm, norms
  // for the legal basis of findings and their AI drafts from legal.
  dependsOn: ['projects', 'crm', 'legal'],
  permissions: [
    // Scope "own": inspections the person inspects or created.
    { key: READ, scopes: ['own', 'all'] },
    { key: WRITE, scopes: ['own', 'all'] },
    { key: 'sigeko.inspection.delete', privileged: true },
    { key: 'sigeko.inspection.comment' },
    { key: 'sigeko.inspection.export' },
    // Catalogs, report templates, questionnaires and the settings of the module.
    { key: 'sigeko.settings.manage', privileged: true },
  ],
  defaultRoles: [
    {
      key: 'inspector',
      name: 'sigeko.roles.inspector',
      grants: [
        { permission: READ, scope: 'all' },
        { permission: WRITE, scope: 'own' },
        { permission: 'sigeko.inspection.comment', scope: null },
        { permission: 'sigeko.inspection.export', scope: null },
      ],
    },
    {
      key: 'manager',
      name: 'sigeko.roles.manager',
      grants: [
        // Unscoped permissions of the wildcard ignore the scope.
        { permission: 'sigeko.inspection.*', scope: 'all' },
        { permission: 'sigeko.settings.manage', scope: null },
      ],
    },
  ],
  navigation: [
    {
      label: 'sigeko.nav.inspections',
      path: '/sigeko/inspections',
      icon: 'hard-hat',
      permission: READ,
    },
  ],
  // Construction sites are projects: on the project page first, on the object page after
  // fire safety, whose inspections belong to the object.
  recordSections: [
    {
      key: 'sigeko.project-inspections',
      target: 'projects.project',
      title: 'sigeko.sections.inspections',
      permission: READ,
      order: 10,
    },
    {
      key: 'sigeko.object-inspections',
      target: 'projects.object',
      title: 'sigeko.sections.inspections',
      permission: READ,
      order: 20,
    },
  ],
  search: [{ entity: 'sigeko.inspection', permission: READ }],
  files: [
    {
      entity: 'sigeko.inspection',
      read: READ,
      write: WRITE,
      kinds: ['pdf', 'image'],
      maxBytes: 25_000_000,
    },
  ],
  activity: [
    {
      entity: 'sigeko.inspection',
      read: READ,
      comment: 'sigeko.inspection.comment',
      path: '/sigeko/inspections/$id',
    },
  ],
  views: [{ list: 'sigeko.inspections', read: READ, share: 'sigeko.settings.manage' }],
  findings: [{ entity: 'sigeko.inspection', read: READ, write: WRITE }],
  documents: [
    {
      key: 'sigeko.inspection-report',
      entity: 'sigeko.inspection',
      label: 'sigeko.documents.inspectionReport',
      permission: READ,
      placeholders: [
        ...placeholders('project', ['number', 'title']),
        ...placeholders('object', [
          'name',
          'number',
          'customerNumber',
          'address',
          'street',
          'postalCode',
          'city',
          'state',
          'usage',
          'buildingClass',
        ]),
        // Client of the project, else operator, else owner of the object.
        ...placeholders('customer', [
          'name',
          'number',
          'address',
          'phone',
          'email',
          'website',
          'contact',
        ]),
        ...placeholders('inspection', [
          'title',
          'date',
          'inspectors',
          'participants',
          'attendees',
          'status',
          'remarks',
        ]),
        ...placeholders('findings', ['count', 'open']),
      ],
      sources: [
        { key: 'attendees', kind: 'table', label: 'sigeko.documents.sources.attendees' },
        { key: 'remarks', kind: 'text', label: 'sigeko.documents.sources.remarks' },
      ],
    },
  ],
  questionnaires: [
    {
      key: 'sigeko.general',
      entity: 'sigeko.inspection',
      label: 'sigeko.questionnaires.general',
      document: 'sigeko.inspection-report',
    },
  ],
  offline: [
    {
      entity: 'sigeko.inspection',
      label: 'sigeko.offline.inspections',
      mode: 'read-write',
      // Only prepared inspections: one prepared itself, or those of a prepared project or object
      // (InspectionOffline).
      scope: 'prepared',
      conflict: 'field-merge',
      maxAge: 'P30D',
      permissions: { read: READ, write: WRITE },
      pins: [
        { entity: 'projects.project', field: 'projectId' },
        { entity: 'projects.object', field: 'objectId' },
        // An inspection prepared itself, with the plans of its project and object.
        { entity: 'sigeko.inspection', field: 'id' },
      ],
    },
  ],
  ai: [
    {
      // Drafts a finding from notes taken on site. Notes describe the site, not people; email
      // addresses, phone numbers and IBANs are replaced anyway.
      key: 'sigeko.finding-draft',
      label: 'sigeko.ai.findingDraft',
      data: 'none',
      entities: ['sigeko.inspection'],
      permission: WRITE,
      // One finding from bullet points with a fixed answer shape.
      model: 'small',
    },
  ],
  events: {
    publishes: [
      inspectionCreated.type,
      inspectionUpdated.type,
      inspectionStatusChanged.type,
      inspectionDeleted.type,
    ],
  },
  dataProcessing: [
    {
      entity: 'sigeko.inspection',
      purpose:
        'Nachweis der SiGeKo-Begehungen auf Baustellen gegenüber Auftraggeber und Behörden: Begeher, Anwesende und Teilnehmer, Bemerkungen, Allgemeine Angaben, Feststellungen mit Fotos, GPS-Punkt, Verantwortlichen und Fristen sowie Anhänge; KI-Mangelerfassung aus Stichpunkten',
      legalBasis: 'Art. 6 Abs. 1 lit. b und f DSGVO',
      // Inspectors are account IDs; attendees and participants are names and functions of third
      // parties, as is who remedies a finding; remarks, answers and findings are free text; photos can show people. Findings of
      // georeferenced inspections carry a GPS point, where the inspector stood or took the photo;
      // photos keep the GPS position where they were taken.
      categories: ['account-id', 'name', 'job-title', 'free-text', 'photos', 'location'],
      // Proposal: ten years after the report was sent (liability, records), inspections never
      // completed ten years after their date. Still to be confirmed with the DPO (open question 55).
      retention: { period: 'P10Y', after: 'completed' },
      erase: 'anonymize',
      // Notes for the AI draft of a finding go to the AI provider the company picked.
      processors: ['ai-provider'],
    },
  ],
});

/** Placeholders of a group of the report, with labels and group in the module texts. */
function placeholders(group: string, names: readonly string[]) {
  return names.map((name) => ({
    key: `${group}.${name}`,
    label: `sigeko.documents.placeholders.${group}.${name}`,
    group: `sigeko.documents.groups.${group}`,
  }));
}
